import {Component, EventEmitter, Input, Output} from '@angular/core';
import {HttpClient} from '@angular/common/http';

/** Oferta combinada para o lead (espelha OfertaDoLead do servidor). Tudo null = oferta padrão. */
export interface OfertaDoLead {
  ofertaValor: number | null;
  ofertaValorInicial: number | null;
  ofertaMesesIniciais: number | null;
}

export interface SugestaoDeOferta extends OfertaDoLead { motivo: string; }

/** Mensalidade da oferta vigente; o servidor tem o mesmo número em OFERTA_PADRAO_MENSAL. */
export const OFERTA_PADRAO_MENSAL = 124.9;

export function formateReais(valor: number): string { return `R$ ${Number(valor).toFixed(2).replace('.', ',')}`; }

export function temOfertaPropria(o: OfertaDoLead | null | undefined): boolean {
  return !!o && ((o.ofertaValor !== null && o.ofertaValor !== undefined) || !!o.ofertaValorInicial);
}

/** A oferta em uma frase curta, como o cartão e o painel mostram ("R$ 99,90 × 3 meses, depois R$ 124,90"). */
export function descrevaOferta(o: OfertaDoLead | null | undefined): string {
  if (!temOfertaPropria(o)) return `${formateReais(OFERTA_PADRAO_MENSAL)}/mês (padrão)`;
  const mensal = formateReais(o.ofertaValor ?? OFERTA_PADRAO_MENSAL);
  if (o.ofertaValorInicial && o.ofertaMesesIniciais) {
    const m = Number(o.ofertaMesesIniciais);
    return `${formateReais(o.ofertaValorInicial)} × ${m} ${m === 1 ? 'mês' : 'meses'}, depois ${mensal}/mês`;
  }
  return `${mensal}/mês`;
}

/**
 * Oferta para o lead: leitura em uma linha, edição no lugar (mensalidade e, opcionalmente, valor menor nos
 * primeiros meses) e "Mia sugere", que só preenche o formulário; gravar é sempre um clique da equipe.
 * Usado no painel do contato da extensão e na gaveta do kanban: quem usa passa as URLs e recebe o lead salvo.
 */
@Component({
  selector: 'app-oferta-lead-gestor',
  templateUrl: './oferta-lead-gestor.component.html',
  styleUrls: ['./oferta-lead-gestor.component.scss'],
})
export class OfertaLeadGestorComponent {
  @Input() oferta: OfertaDoLead | null = null;
  /** PUT que grava a oferta e devolve {lead} */
  @Input() urlSalvar = '';
  /** POST que pede a sugestão à Mia e devolve {sugestao}; vazio esconde o botão */
  @Input() urlSugerir = '';
  /** false quando a Mia ainda não conversou com o lead (sem ficha não há sugestão) */
  @Input() podeSugerir = true;
  @Output() salvo = new EventEmitter<any>();
  @Output() falhou = new EventEmitter<string>();

  editando = false;
  salvando = false;
  sugerindo = false;
  erro = '';
  /** campos do formulário como texto: o input numérico devolve string e o vazio precisa continuar vazio */
  valor = '';
  comInicial = false;
  valorInicial = '';
  meses = '3';
  /** motivo da última sugestão da Mia; some quando a equipe mexe nos campos */
  motivoSugestao = '';

  constructor(private http: HttpClient) {}

  get textoAtual(): string { return descrevaOferta(this.oferta); }
  get ehPadrao(): boolean { return !temOfertaPropria(this.oferta); }

  /** O que vai ser gravado, lido do formulário; null quando algo está inválido. */
  get ofertaDoFormulario(): OfertaDoLead | null {
    const numero = (t: string): number | null | undefined => {
      const s = String(t || '').trim().replace(',', '.');
      if (!s) return null;
      const n = Number(s);
      return Number.isFinite(n) && n > 0 ? n : undefined;
    };
    const valor = numero(this.valor);
    const inicial = this.comInicial ? numero(this.valorInicial) : null;
    const meses = this.comInicial ? numero(this.meses) : null;
    if (valor === undefined || inicial === undefined || meses === undefined) return null;
    if (this.comInicial && (inicial === null || meses === null || !Number.isInteger(meses) || meses > 24)) return null;
    return {ofertaValor: valor, ofertaValorInicial: inicial, ofertaMesesIniciais: meses};
  }

  get previa(): string { const o = this.ofertaDoFormulario; return o ? descrevaOferta(o) : ''; }

  abra() {
    this.preencha(this.oferta);
    this.motivoSugestao = ''; this.erro = ''; this.editando = true;
  }

  cancele() { this.editando = false; this.motivoSugestao = ''; this.erro = ''; }

  /** "Voltar ao padrão": limpa os campos; gravar continua sendo o Salvar. */
  limpe() { this.preencha(null); this.motivoSugestao = ''; }

  mexeu() { this.motivoSugestao = ''; }

  private preencha(o: OfertaDoLead | null) {
    const texto = (n: number | null | undefined) => n === null || n === undefined ? '' : String(n).replace('.', ',');
    this.valor = texto(o?.ofertaValor);
    this.comInicial = !!o?.ofertaValorInicial;
    this.valorInicial = texto(o?.ofertaValorInicial);
    this.meses = o?.ofertaMesesIniciais ? String(o.ofertaMesesIniciais) : '3';
  }

  salve() {
    const o = this.ofertaDoFormulario;
    if (!o || this.salvando || !this.urlSalvar) return;
    this.salvando = true; this.erro = '';
    this.http.put<{lead: any}>(this.urlSalvar, o).subscribe({
      next: r => {
        this.salvando = false; this.editando = false; this.motivoSugestao = '';
        this.oferta = {ofertaValor: r.lead?.ofertaValor ?? null, ofertaValorInicial: r.lead?.ofertaValorInicial ?? null, ofertaMesesIniciais: r.lead?.ofertaMesesIniciais ?? null};
        this.salvo.emit(r.lead);
      },
      error: e => { this.salvando = false; this.erro = e?.error?.erro || 'Não foi possível salvar a oferta.'; this.falhou.emit(this.erro); },
    });
  }

  /** A Mia lê ficha e conversa e preenche o formulário; a equipe revisa e salva. */
  sugira() {
    if (this.sugerindo || !this.urlSugerir) return;
    this.sugerindo = true; this.erro = '';
    if (!this.editando) { this.preencha(this.oferta); this.editando = true; }
    this.http.post<{sugestao: SugestaoDeOferta}>(this.urlSugerir, {}).subscribe({
      next: r => {
        this.sugerindo = false;
        this.preencha(r.sugestao);
        this.motivoSugestao = r.sugestao?.motivo || 'Sugestão da Mia.';
      },
      error: e => { this.sugerindo = false; this.erro = e?.error?.erro || 'A Mia não conseguiu sugerir agora.'; },
    });
  }
}
